import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import MetricSource from './MetricSource';
import { figures, FigureId } from '../../data/figures';
import { ChorieDemo, FindyDemo, LeadsDemo, RadarDemo, SaidaDemo } from './ProductDemos';
import * as S from './styles';

const products = [
  {
    id: 'imgradar',
    name: 'IMG Radar',
    icon: '/assets/brand/imgradar-mark.svg',
    category: 'RESEARCH PLATFORM',
    headline: 'Find the programs. Check the evidence.',
    description:
      'I built a research platform for internationally trained doctors applying to US residency. Automated research, source verification and subscriptions in one product.',
    figureId: 'imgradar.programs' as FigureId,
    label: 'residency programs mapped',
    secondary: `${figures['imgradar.confirmedPct'].value} of research findings confirmed by a second reading. ${figures['imgradar.subscribers'].value} paying subscribers in the first month.`,
    href: 'https://imgradar.com',
    cta: 'Visit IMG Radar',
    demo: RadarDemo,
  },
  {
    id: 'findy',
    name: 'Findy',
    icon: '/assets/brand/findy-mark.svg',
    category: 'PERSONAL AI · WEB & IOS',
    headline: 'An answer is only as good as its source.',
    description:
      'I built a document assistant that organizes the drawer and answers with a file and a page. Try a question the sample documents can answer, then one they cannot.',
    figureId: 'findy.tests' as FigureId,
    label: 'backend tests against PostgreSQL and pgvector',
    secondary: `${figures['findy.tenantAssertions'].value} tenant-isolation checks. Citations required for every answer.`,
    href: 'https://askfindy.com',
    cta: 'Visit Findy',
    app: `https://apps.apple.com/app/${figures['findy.appStoreId'].value}`,
    demo: FindyDemo,
  },
  {
    id: 'homechores',
    name: 'HomeChores',
    icon: '/assets/brand/chorie-avatar.webp',
    category: 'FAMILY APP · IOS & ANDROID',
    headline: 'Built for the work of running a home.',
    description:
      'A shared household app with an assistant that turns everyday requests into tasks, shopping lists and plans. Built, launched and grown without a team.',
    figureId: 'chorie.publishedUsers' as FigureId,
    label: 'users',
    secondary: '978 signups in one month from one organic video.',
    href: 'https://homechoresapp.com',
    cta: 'Visit HomeChores',
    app: `https://apps.apple.com/app/${figures['chorie.appStoreId'].value}`,
    demo: ChorieDemo,
  },
  {
    id: 'saidacerta',
    name: 'Saída Certa',
    icon: '/assets/brand/saidacerta-seal-96.png',
    category: 'TAX DEPARTURE PLATFORM',
    headline: 'A filing process, made navigable.',
    description:
      'A guided platform for Brazilian tax departure. I built the fiscal engine, document workflow and payment integration, with deterministic calculations at the core.',
    figureId: 'saida.goldenVectors' as FigureId,
    label: 'reference cases validating the fiscal engine',
    secondary: 'Matching results in Python and TypeScript. Deterministic calculations.',
    href: 'https://saidacerta.com',
    cta: 'Visit Saída Certa',
    demo: SaidaDemo,
  },
  {
    id: 'radar407',
    name: 'radar407',
    icon: '/assets/brand/radar407-icon.svg',
    category: 'DATA PIPELINE · CENTRAL FLORIDA',
    headline: 'Public records. A daily business advantage.',
    description:
      'An unattended pipeline turns permits and notices into leads for local contractors. Collection, license matching and delivery run on AWS.',
    figureId: 'r407.sources' as FigureId,
    label: 'source collectors across 3 counties',
    secondary: 'Operating nightly since August 12, 2026.',
    href: 'https://www.linkedin.com/in/guinssa/',
    cta: 'Ask me about radar407',
    demo: LeadsDemo,
  },
];

export default function Showcase() {
  const { hash } = useLocation();
  const [active, setActive] = useState(0);
  useEffect(() => {
    const index = products.findIndex(p => `#${p.id}` === hash);
    if (index >= 0) {
      setActive(index);
      document.getElementById('work')?.scrollIntoView();
    }
  }, [hash]);
  const product = products[active];
  const Demo = product.demo;
  const select = (index: number) => {
    setActive(index);
  };
  return (
    <>
      <S.ProductTabs role="tablist" aria-label="Explore five products">
        {products.map((p, i) => (
          <button
            key={p.id}
            id={`tab-${p.id}`}
            role="tab"
            aria-selected={active === i}
            aria-controls="product-panel"
            tabIndex={active === i ? 0 : -1}
            onClick={() => select(i)}
            onKeyDown={e => {
              let next = i;
              if (e.key === 'ArrowRight') next = (i + 1) % products.length;
              else if (e.key === 'ArrowLeft') next = (i + products.length - 1) % products.length;
              else if (e.key === 'Home') next = 0;
              else if (e.key === 'End') next = products.length - 1;
              else return;
              e.preventDefault();
              select(next);
              document.getElementById(`tab-${products[next].id}`)?.focus();
            }}
          >
            <img src={p.icon} alt="" />
            {p.name === 'HomeChores' ? 'Home Chores' : p.name}
            <span>0{i + 1}</span>
          </button>
        ))}
      </S.ProductTabs>
      <S.Showcase
        id="product-panel"
        role="tabpanel"
        aria-labelledby={`tab-${product.id}`}
        tabIndex={0}
      >
        <S.ProductCopy>
          <S.Kicker>{product.category}</S.Kicker>
          <h3>{product.headline}</h3>
          <p>{product.description}</p>
          <S.ProductMetric>
            <MetricSource key={product.id} id={product.figureId} />
            <span>{product.label}</span>
          </S.ProductMetric>
          <small>{product.secondary}</small>
          <S.Actions>
            <S.ButtonLink href={product.href} target="_blank" rel="noopener noreferrer">
              {product.cta} ↗
            </S.ButtonLink>
            {product.app && (
              <S.TextLink href={product.app} target="_blank" rel="noopener noreferrer">
                App Store ↗
              </S.TextLink>
            )}
          </S.Actions>
          <S.DemoCaption>Figures from the project records, June–September 2026.</S.DemoCaption>
        </S.ProductCopy>
        <div>
          <Demo key={product.id} />
          <S.DemoCaption>
            {product.id === 'imgradar'
              ? 'Interactive published snapshot. Explore current program details on IMG Radar.'
              : product.id === 'saidacerta'
              ? 'A short workflow preview. Full product available on Saída Certa.'
              : 'Interactive sample. The full experience lives in the product.'}
          </S.DemoCaption>
        </div>
      </S.Showcase>
    </>
  );
}
